import { useCallback, useMemo, useState } from 'react';
import { message } from 'antd';
import {
  fetchPresets,
  savePresetViaApi,
  deletePresetViaApi,
  FilterPreset,
  PresetScope,
} from '@/pages/Data/components/presets.api';
import { useToken } from '@/common/helper';
import type { OptionItem } from '@/pages/Data/components/DownloadReportFilterSection';
import {
  AppliedFilter,
  FilterArrayItem,
} from '@/pages/Shipments/ControlTower/types';
import { buildSavedFilterTooltip } from '@/pages/Shipments/ControlTower/utils/filterPresetTooltip';

interface SaveArgs {
  name: string;
  filterArray: FilterArrayItem[];
  appliedFilters: AppliedFilter[];
}

interface DeleteOptions {
  onAfterDelete?: (next: FilterPreset[]) => void;
}

interface UseFilterPresetsOptions {
  type?: PresetScope | string;
}

const deriveAppliedFiltersFromFilterArray = (
  filterArray: FilterArrayItem[]
): AppliedFilter[] =>
  filterArray.flatMap((item) => {
    const values = Array.isArray(item?.filterValue)
      ? item.filterValue
      : item?.filterValue != null
        ? [item.filterValue]
        : [];

    return values.map((value) => ({
      id: String(value),
      name: String(value),
      filterBy: item.filterBy,
      groupTitle: item.filterBy,
    }));
  });

const getPresetFiltersObject = (
  filters: FilterPreset['filters']
): {
  filterArray: FilterArrayItem[];
  appliedFilters: AppliedFilter[];
} | null => {
  if (!filters || Array.isArray(filters)) {
    return null;
  }

  return {
    filterArray: Array.isArray(filters.filterArray) ? filters.filterArray : [],
    appliedFilters: Array.isArray(filters.appliedFilters)
      ? filters.appliedFilters
      : [],
  };
};

export const buildPresetOptions = (
  presets: FilterPreset[],
  presetType?: PresetScope | string
): OptionItem[] =>
  (presets || []).map((p) => {
    if (presetType === 'shipment') {
      const filtersSource = p.filters;
      const filtersObject = getPresetFiltersObject(filtersSource);

      const filterArray: FilterArrayItem[] = Array.isArray(filtersSource)
        ? filtersSource
        : (filtersObject?.filterArray ??
          (Array.isArray(p.filterArray) ? p.filterArray : []));

      const rawPresets: AppliedFilter[] = Array.isArray(p.presets)
        ? p.presets
        : [];

      const explicitAppliedFilters: AppliedFilter[] =
        filtersObject?.appliedFilters ??
        (Array.isArray(p.appliedFilters) ? p.appliedFilters : []);

      const tooltipAppliedFilters: AppliedFilter[] =
        rawPresets.length > 0
          ? rawPresets
          : explicitAppliedFilters.length > 0
            ? explicitAppliedFilters
            : deriveAppliedFiltersFromFilterArray(filterArray);

      const flattenedFilterValuesCount = filterArray.reduce((acc, item) => {
        const values = Array.isArray(item?.filterValue)
          ? item.filterValue
          : item?.filterValue != null
            ? [item.filterValue]
            : [];

        return acc + values.length;
      }, 0);

      const filterCount = flattenedFilterValuesCount;

      return {
        id: String(p.id),
        name: p.name,
        title: p.name,
        _isPreset: true,
        filterCount,
        appliedFilters: tooltipAppliedFilters,
        filterArray,
        tooltipText:
          tooltipAppliedFilters.length > 0
            ? buildSavedFilterTooltip(tooltipAppliedFilters)
            : '',
      };
    }

    const nonShipmentFilters = p.filters;
    const nonShipmentFiltersObject = getPresetFiltersObject(nonShipmentFilters);

    const appliedFilters: AppliedFilter[] =
      nonShipmentFiltersObject?.appliedFilters ??
      (Array.isArray(p.appliedFilters) ? p.appliedFilters : []);

    const filterArray: FilterArrayItem[] =
      nonShipmentFiltersObject?.filterArray ??
      (Array.isArray(p.filterArray) ? p.filterArray : []);

    const filterCount = Math.max(appliedFilters.length, filterArray.length);

    return {
      id: String(p.id),
      name: p.name,
      title: p.name,
      _isPreset: true,
      filterCount,
      appliedFilters,
      filterArray,
      tooltipText:
        filterCount > 0 ? buildSavedFilterTooltip(appliedFilters) : '',
    };
  });

export default function useFilterPresets(options?: UseFilterPresetsOptions) {
  const { token } = useToken();

  const presetType = (options?.type ?? 'report') as PresetScope;

  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [savePresetOpen, setSavePresetOpen] = useState(false);

  const loadPresets = useCallback(async (): Promise<FilterPreset[]> => {
    if (!token) {
      return [];
    }

    try {
      const list = await fetchPresets(token, presetType);
      const safeList = list || [];
      setPresets(safeList);
      return safeList;
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [token, presetType]);

  const presetOptions: OptionItem[] = useMemo(
    () => buildPresetOptions(presets, presetType),
    [presets, presetType]
  );

  const openSavePresetModal = () => setSavePresetOpen(true);
  const closeSavePresetModal = () => setSavePresetOpen(false);

  const handleSavePreset = useCallback(
    async ({
      name,
      filterArray,
      appliedFilters,
    }: SaveArgs): Promise<boolean> => {
      if (!token) {
        message.error('Not authenticated');
        return false;
      }

      try {
        const trimmedName = name.trim();

        if (!trimmedName) {
          message.error('Please enter a name for the filter set');
          return false;
        }

        const now = Date.now();
        const existing = selectedPresetId
          ? presets.find((p) => String(p.id) === String(selectedPresetId))
          : null;

        const base = existing
          ? {
              ...existing,
              name: trimmedName,
              filterArray: JSON.parse(JSON.stringify(filterArray || [])),
              appliedFilters: JSON.parse(JSON.stringify(appliedFilters || [])),
              updatedAt: now,
            }
          : {
              id: '',
              name: trimmedName,
              filterArray: JSON.parse(JSON.stringify(filterArray || [])),
              appliedFilters: JSON.parse(JSON.stringify(appliedFilters || [])),
              createdAt: now,
              updatedAt: now,
              isDefault: false,
            };

        const overwriteId = existing ? existing.id : null;

        const saved = await savePresetViaApi(token, base, {
          overwriteId,
          scope: presetType,
        });

        await loadPresets();

        setSelectedPresetId(String(saved.id));
        message.success(overwriteId ? 'Preset overwritten' : 'Filters saved');
        setSavePresetOpen(false);

        return true;
      } catch (err) {
        console.error(err);
        message.error('Failed to save preset');
        return false;
      }
    },
    [presets, selectedPresetId, token, loadPresets, presetType]
  );

  const handleDeletePresetById = useCallback(
    async (id: string | number, options?: DeleteOptions) => {
      if (!token) {
        return [];
      }

      try {
        await deletePresetViaApi(token, String(id));
        const next = presets.filter((p) => String(p.id) !== String(id));
        setPresets(next);

        if (selectedPresetId === String(id)) {
          setSelectedPresetId(null);
        }

        message.success('Preset deleted');

        if (options?.onAfterDelete) {
          options.onAfterDelete(next);
        }

        return next;
      } catch (err) {
        console.error(err);
        message.error('Failed to delete preset');
        throw err;
      }
    },
    [token, presets, selectedPresetId]
  );

  const handleDeletePresetAndSyncOptions = useCallback(
    async (
      id: string | number,
      params: {
        filterBy: string;
        setSecondaryFiltersSource: React.Dispatch<
          React.SetStateAction<OptionItem[]>
        >;
        setSecondaryFiltersDDL: React.Dispatch<
          React.SetStateAction<OptionItem[]>
        >;
      }
    ) => {
      const { filterBy, setSecondaryFiltersSource, setSecondaryFiltersDDL } =
        params;

      return handleDeletePresetById(id, {
        onAfterDelete: (next) => {
          if (filterBy === 'saved_filters') {
            const options = buildPresetOptions(next, presetType);
            setSecondaryFiltersSource(options);
            setSecondaryFiltersDDL(options);
          }
        },
      });
    },
    [handleDeletePresetById, presetType]
  );

  return {
    presets,
    presetOptions,
    selectedPresetId,
    savePresetOpen,
    setSelectedPresetId,
    setSavePresetOpen,
    openSavePresetModal,
    closeSavePresetModal,
    loadPresets,
    handleSavePreset,
    handleDeletePresetById,
    handleDeletePresetAndSyncOptions,
  };
}
